import { useId, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

type LoaderSize = 'xs' | 'sm' | 'md' | 'lg';
type LoaderVariant = 'brand' | 'white' | 'muted';

interface LoaderProps extends HTMLAttributes<HTMLDivElement> {
  size?: LoaderSize;
  variant?: LoaderVariant;
  label?: string;
  showLabel?: boolean;
}

const sizeClasses: Record<LoaderSize, string> = {
  xs: 'h-4 w-4',
  sm: 'h-5 w-5',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

const labelClasses: Record<LoaderVariant, string> = {
  brand: 'text-brand-navy',
  white: 'text-white',
  muted: 'text-brand-muted',
};

/** Branded AS DataLink spinner — shared across user app and admin. */
export function Loader({
  size = 'md',
  variant = 'brand',
  label = 'Loading',
  showLabel = false,
  className,
  ...props
}: LoaderProps) {
  const gradientId = useId();

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      className={cn('inline-flex flex-col items-center justify-center gap-2', className)}
      {...props}
    >
      <svg
        className={cn('animate-spin', sizeClasses[size])}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="text-gray-200" />
        {variant === 'brand' ? (
          <>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F97015" />
                <stop offset="100%" stopColor="#21C45D" />
              </linearGradient>
            </defs>
            <path
              d="M12 2a10 10 0 0 1 10 10"
              stroke={`url(#${gradientId})`}
              strokeWidth="3"
              strokeLinecap="round"
            />
          </>
        ) : (
          <path
            d="M12 2a10 10 0 0 1 10 10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            className={variant === 'white' ? 'text-white' : 'text-brand-muted'}
          />
        )}
      </svg>
      {showLabel && <span className={cn('text-sm font-medium', labelClasses[variant])}>{label}</span>}
      <span className="sr-only">{label}</span>
    </div>
  );
}

interface LoadingOverlayProps {
  show: boolean;
  label?: string;
  variant?: 'light' | 'dark';
  children: ReactNode;
  className?: string;
}

/** Blocks interaction and shows loader over content during API calls. */
export function LoadingOverlay({
  show,
  label = 'Please wait...',
  variant = 'light',
  children,
  className,
}: LoadingOverlayProps) {
  return (
    <div className={cn('relative', className)}>
      {children}
      {show && (
        <div
          className={cn(
            'absolute inset-0 z-20 flex items-center justify-center rounded-2xl backdrop-blur-[2px]',
            variant === 'light' ? 'bg-white/75' : 'bg-slate-950/70',
          )}
          aria-busy="true"
        >
          <Loader size="lg" variant={variant === 'dark' ? 'white' : 'brand'} label={label} showLabel />
        </div>
      )}
    </div>
  );
}

interface PageLoaderProps {
  label?: string;
  variant?: 'light' | 'dark';
}

/** Full-area loader for page-level waits. */
export function PageLoader({ label = 'Loading', variant = 'light' }: PageLoaderProps) {
  return (
    <div className="flex min-h-[240px] flex-col items-center justify-center">
      <Loader size="lg" variant={variant === 'dark' ? 'white' : 'brand'} label={label} showLabel />
    </div>
  );
}
